// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/help/:topic\",\"regex\":\"^/help/([^/]+)$\",\"paramNames\":[\"topic\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId\",\"regex\":\"^/project/([^/]+)$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/albums\",\"regex\":\"^/project/([^/]+)/albums$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/angles\",\"regex\":\"^/project/([^/]+)/angles$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/concept/:conceptId\",\"regex\":\"^/project/([^/]+)/concept/([^/]+)$\",\"paramNames\":[\"projectId\",\"conceptId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/concepts\",\"regex\":\"^/project/([^/]+)/concepts$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/estimates\",\"regex\":\"^/project/([^/]+)/estimates$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/goals\",\"regex\":\"^/project/([^/]+)/goals$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:projectId/references\",\"regex\":\"^/project/([^/]+)/references$\",\"paramNames\":[\"projectId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/account\"},{\"path\":\"/account/agent-keys\"},{\"path\":\"/account/appearance\"},{\"path\":\"/account/branding\"},{\"path\":\"/account/delete\"},{\"path\":\"/account/feedback\"},{\"path\":\"/account/language\"},{\"path\":\"/account/payments\"},{\"path\":\"/account/privacy\"},{\"path\":\"/account/purchase-history\"},{\"path\":\"/account/sign-in-methods\"},{\"path\":\"/account/terms\"},{\"path\":\"/account/tokens\"},{\"path\":\"/account/usage-stats\"},{\"path\":\"/connect\"},{\"path\":\"/help\"},{\"path\":\"/history\"},{\"path\":\"/history/trash\"},{\"path\":\"/start\"},{\"path\":\"/activate\"},{\"path\":\"/auth/apple/callback\"},{\"path\":\"/auth/auto-login\"},{\"path\":\"/auth/forgot-password\"},{\"path\":\"/auth/google/callback\"},{\"path\":\"/auth/login\"},{\"path\":\"/auth/register\"},{\"path\":\"/auth/reset-password\"},{\"path\":\"/auth/verify-email\"},{\"path\":\"/auth/vk/callback\"},{\"path\":\"/dev/assistant\"},{\"path\":\"/dev/trim\"},{\"path\":\"/legal/pricing\"},{\"path\":\"/legal/privacy\"},{\"path\":\"/legal/terms\"},{\"path\":\"/payment/tbank/return\"},{\"path\":\"/pwa-test\"},{\"path\":\"/unsupported-browser\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.8";import * as Sentry from '@sentry/nextjs';
import { ApiClientError } from '@/shared/lib/api';

// HTTP statuses that represent expected business outcomes, not bugs:
//   401 — auth required        402 — payment required
//   403 — forbidden            422 — validation / typed limit
//   429 — rate-limited
// Per memory `feedback_sentry_business_exceptions`: Sentry should only see
// code+infrastructure problems. Limit/auth/validation responses are designed
// API behaviour and create noise that drowns real bugs.
const BUSINESS_HTTP_STATUSES = new Set([401, 402, 403, 422, 429]);

// Typed `LimitErrorCode` values from `shared/lib/api.ts`. Mirroring the
// union as a runtime Set so we can drop these even when the server attached
// the code to a non-business HTTP status (e.g. 200 fallback responses).
const BUSINESS_API_CODES = new Set<string>([
  'ANGLE_LIBRARY_LIMIT_EXCEEDED',
  'CONCEPT_ANGLE_LIMIT_EXCEEDED',
  'PROJECT_LIMIT_EXCEEDED',
  'PROJECT_REFERENCE_LIMIT_EXCEEDED',
  'PROJECT_REFERENCE_INSUFFICIENT_TOKENS',
  'RENDER_QUEUE_LIMIT_EXCEEDED',
  'RENDER_DAILY_LIMIT_EXCEEDED',
  'RENDER_WEEKLY_LIMIT_EXCEEDED',
  'RENDER_MONTHLY_LIMIT_EXCEEDED',
  'SUBSCRIPTION_REQUIRED',
]);

// Only initialize Sentry if DSN is configured
if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Environment
    environment:
      process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',

    // Release tracking
    release: process.env.NEXT_PUBLIC_APP_VERSION,

    // Replay configuration for session recording
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0,

    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        // Block cross-origin iframes (reCAPTCHA, analytics, ads) —
        // Replay crashes when trying to observe their DOM (SecurityError / TypeError)
        block: [
          'iframe[src*="google.com"]',
          'iframe[src*="recaptcha"]',
          'iframe[src*="doubleclick"]',
          'iframe[src*="youtube.com"]',
        ],
      }),
      Sentry.browserTracingIntegration(),
    ],

    // Filter out specific errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Drop business-flow ApiClientError responses. Limit/auth/validation
      // outcomes are deliberate server behaviour driving UI (paywalls, login
      // prompts, retry banners) — not exceptions Sentry should page on.
      if (error instanceof ApiClientError) {
        if (
          BUSINESS_HTTP_STATUSES.has(error.status) ||
          (error.code && BUSINESS_API_CODES.has(error.code))
        ) {
          return null;
        }
      }

      // Parse errors from script the page never shipped. Our bundle is parsed
      // once at load; a runtime SyntaxError can only come from code injected
      // and eval'd into the document by something else — in-app browser hosts
      // (LANDING-1B: "Unexpected identifier 'https'" from a Chrome WebView 117
      // on a Samsung, a single frame at `app:///` line 1 with no `/_next/`
      // frame anywhere) and wallet extensions. Same class as the `inpage.js`
      // denyUrl below, but these land on the document URL instead, so no URL
      // rule can catch them — the absence of one of our own chunks in the
      // stack is the signal.
      const values = event.exception?.values ?? [];
      if (
        values.length > 0 &&
        values.every((v) => v.type === 'SyntaxError') &&
        !values.some((v) =>
          v.stacktrace?.frames?.some((f) => (f.filename ?? '').includes('/_next/')),
        )
      ) {
        return null;
      }

      // Crashes inside Google's reCAPTCHA bundle (seen on the landing as
      // "reading 'h6'" from a headless datacenter browser). The throwing frame
      // is `recaptcha__<lang>.js`; the only `/_next/` frame is Sentry's own
      // setTimeout wrapper on top, so `denyUrls` (top frame) misses it.
      // Nothing in our code can fix a fault in Google's minified internals.
      if (
        values.some((v) =>
          v.stacktrace?.frames?.some((f) => (f.filename ?? '').includes('/recaptcha/releases/')),
        )
      ) {
        return null;
      }

      // Filter out network errors that are not actionable
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);

        // Ignore Sentry Replay cross-origin iframe errors —
        // Replay tries to observe DOM inside cross-origin iframes and crashes
        if (
          message.includes('Blocked a frame with origin') ||
          (message.includes("Cannot read properties of undefined (reading 'prototype')") &&
            event.exception?.values?.some((v) =>
              v.stacktrace?.frames?.some((f) =>
                f.module?.includes('@sentry-internal/replay'),
              ),
            ))
        ) {
          return null;
        }

        // Ignore common browser extension errors
        if (
          message.includes('chrome-extension://') ||
          message.includes('moz-extension://')
        ) {
          return null;
        }

        // Ignore ResizeObserver errors (harmless browser quirks)
        if (message.includes('ResizeObserver')) {
          return null;
        }

        // Ignore cancelled requests (user navigation)
        if (message.includes('cancelled') || message.includes('aborted')) {
          return null;
        }
      }

      return event;
    },

    // Ignore specific errors by type
    ignoreErrors: [
      // Sentry Replay cross-origin iframe errors
      /Blocked a frame with origin.*from accessing a cross-origin frame/,
      // Browser extensions
      'top.GLOBALS',
      'atomicFindClose',
      // Random plugins/extensions
      'NonError: Object captured as promise rejection',
      // Network errors
      'NetworkError',
      'Network request failed',
    ],

    // Deny list for URLs that should not be sent to Sentry
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Wallet/crypto extensions inject into the page context, so their errors
      // surface as `app:///inpage.js`, not chrome-extension:// — see landing.
      /inpage\.js/i,
    ],

    // Add custom tags
    initialScope: {
      tags: {
        app: 'web',
      },
    },
  });
}

// Export navigation instrumentation hook for App Router
// https://docs.sentry.io/platforms/javascript/guides/nextjs/features/instrumentation/
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
